import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarCheck,
  CalendarDays,
  CalendarRange,
  ChevronRight,
  Ticket,
} from 'lucide-react';
import { Card } from '@shared/ui/containers/Card';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { Button } from '@shared/ui/primitives/Button';
import { Dropdown } from '@shared/ui/primitives/Dropdown';
import { toast } from '@shared/ui/feedback/Toast';
import { pageCopy } from '@shared/lib/pageCopy';
import {
  clearFacilityBooking,
  cancelFacilityBooking,
  fetchFacilities,
  fetchMyBookings,
  type Facility,
  type FacilityBooking,
  type FacilityBookingStatus,
} from './api';
import { BookingDetailSheet, STATUS_LABEL, STATUS_TONE, type BookingSheetSlot } from './BookingDetailSheet';

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});
const TIME_FMT = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
const STATUS_FILTER_OPTIONS: Array<{ value: 'all' | FacilityBookingStatus; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'pending', label: STATUS_LABEL.pending },
  { value: 'confirmed', label: STATUS_LABEL.confirmed },
  { value: 'approved', label: STATUS_LABEL.approved },
  { value: 'rejected', label: STATUS_LABEL.rejected },
  { value: 'cancelled', label: STATUS_LABEL.cancelled },
  { value: 'completed', label: STATUS_LABEL.completed },
];

/** Resident's own facility bookings — upcoming + past, with cancel. */
export function FacilityMyBookings(): JSX.Element {
  const copy = pageCopy().facilities.myBookings;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [detailBookingId, setDetailBookingId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | FacilityBookingStatus>('all');
  const query = useQuery({ queryKey: ['my-bookings'], queryFn: fetchMyBookings });
  const facilitiesQuery = useQuery({ queryKey: ['facilities'], queryFn: fetchFacilities });

  const cancelMutation = useMutation({
    mutationFn: (bookingId: string) => cancelFacilityBooking(bookingId),
    onSuccess: () => {
      setDetailBookingId(null);
      void queryClient.invalidateQueries({ queryKey: ['my-bookings'] });
      toast.success('Booking cancelled');
    },
    onError: () => toast.danger('Could not cancel', 'Please try again.'),
  });

  const clearMutation = useMutation({
    mutationFn: (bookingId: string) => clearFacilityBooking(bookingId),
    onSuccess: () => {
      setDetailBookingId(null);
      void queryClient.invalidateQueries({ queryKey: ['my-bookings'] });
      toast.success('Booking cleared');
    },
    onError: () => toast.danger('Could not clear booking', 'Please try again.'),
  });

  const bookings = [...(query.data ?? [])].sort((a, b) => b.starts_at.localeCompare(a.starts_at));
  const filteredBookings = useMemo(
    () => (statusFilter === 'all' ? bookings : bookings.filter((booking) => booking.status === statusFilter)),
    [bookings, statusFilter],
  );
  const facilitiesById = useMemo(
    () => new Map((facilitiesQuery.data ?? []).map((facility) => [facility.facility_id, facility])),
    [facilitiesQuery.data],
  );
  const detailBooking = bookings.find((booking) => booking.facility_booking_id === detailBookingId) ?? null;
  const detailFacility = detailBooking
    ? facilitiesById.get(detailBooking.facility_id) ?? facilityStubFromBooking(detailBooking)
    : null;
  const detailSlot: BookingSheetSlot | null = detailBooking
    ? {
        id: detailBooking.facility_booking_id,
        starts_at: detailBooking.starts_at,
        ends_at: detailBooking.ends_at,
        booking: detailBooking,
      }
    : null;
  const confirmCancel = (booking: FacilityBooking): void => {
    if (!window.confirm('Cancel this booking?')) return;
    cancelMutation.mutate(booking.facility_booking_id);
  };

  const confirmClear = (booking: FacilityBooking): void => {
    if (!window.confirm('Clear this rejected booking?')) return;
    clearMutation.mutate(booking.facility_booking_id);
  };

  const header = (
    <HeroHeader
      eyebrow={copy.eyebrow}
      title={copy.title}
      subtitle={copy.subtitle || undefined}
      backTo="/app/facilities"
      backLabel="Back to facilities"
      backBehavior="parent"
    />
  );

  return (
    <div className="min-h-full bg-paper">
      {header}

      <div className="px-5 pt-5 pb-safe-or-6">
        <Card noGrain className="mb-4 p-4">
          <p className="eyebrow mb-2.5">Filter by status</p>
          <Dropdown
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as 'all' | FacilityBookingStatus)}
            aria-label="Filter bookings by status"
          >
            {STATUS_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Dropdown>
        </Card>

        {query.isLoading ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </div>
        ) : query.isError ? (
          <ErrorState onRetry={() => query.refetch()} />
        ) : bookings.length === 0 ? (
          <EmptyState
            icon={<Ticket className="h-7 w-7" strokeWidth={1.5} />}
            title="No bookings yet."
            description="Slots you book appear here."
            action={<Button onClick={() => navigate('/app/facilities')}>Browse facilities</Button>}
          />
        ) : filteredBookings.length === 0 ? (
          <EmptyState
            icon={<CalendarDays className="h-7 w-7" strokeWidth={1.5} />}
            title="No bookings with that status."
            description="Try a different status filter."
            action={statusFilter !== 'all' ? (
              <Button
                variant="secondary"
                onClick={() => setStatusFilter('all')}
              >
                Show all bookings
              </Button>
            ) : undefined}
          />
        ) : (
          <div className="flex flex-col gap-3">
            {filteredBookings.map((booking) => (
              <BookingCard
                key={booking.facility_booking_id}
                booking={booking}
                facility={facilitiesById.get(booking.facility_id)}
                cancelling={cancelMutation.isPending && cancelMutation.variables === booking.facility_booking_id}
                clearing={clearMutation.isPending && clearMutation.variables === booking.facility_booking_id}
                onOpen={() => setDetailBookingId(booking.facility_booking_id)}
                onCancel={() => confirmCancel(booking)}
                onClear={() => confirmClear(booking)}
              />
            ))}
          </div>
        )}
      </div>

      {detailFacility ? (
        <BookingDetailSheet
          open={!!detailSlot}
          facility={detailFacility}
          slot={detailSlot}
          confirmLoading={false}
          cancelLoading={cancelMutation.isPending && cancelMutation.variables === detailBooking?.facility_booking_id}
          clearLoading={clearMutation.isPending && clearMutation.variables === detailBooking?.facility_booking_id}
          onOpenChange={(open) => {
            if (!open) setDetailBookingId(null);
          }}
          onConfirm={() => undefined}
          onClear={confirmClear}
          onCancel={confirmCancel}
        />
      ) : null}
    </div>
  );
}

function BookingCard({
  booking,
  facility,
  cancelling,
  clearing,
  onOpen,
  onCancel,
  onClear,
}: {
  booking: FacilityBooking;
  facility?: Facility;
  cancelling: boolean;
  clearing: boolean;
  onOpen: () => void;
  onCancel: () => void;
  onClear: () => void;
}): JSX.Element {
  const start = new Date(booking.starts_at);
  const canCancel =
    booking.status === 'pending' || booking.status === 'confirmed' || booking.status === 'approved';
  const canClear = booking.status === 'rejected';
  const upcoming = start.getTime() > Date.now();
  const endsAt = new Date(booking.ends_at);

  return (
    <Card noGrain className="overflow-hidden p-0">
      <button type="button" onClick={onOpen} className="block w-full text-left">
        <div className="aspect-[16/9] w-full bg-sunken">
          {facility?.photo_url ? (
            <img src={facility.photo_url} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-primary-50">
              <CalendarRange className="h-7 w-7 text-primary-600" strokeWidth={1.5} />
            </div>
          )}
        </div>

        <div className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-display text-headline text-ink truncate">
                {booking.facility_name ?? facility?.name ?? 'Facility booking'}
              </p>
              {facility?.location_name ? (
                <p className="mt-0.5 truncate text-caption text-ink-muted">{facility.location_name}</p>
              ) : null}
              <p className="mt-2 flex items-start gap-1.5 text-body-sm text-ink-secondary">
                <CalendarCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-ink-muted" strokeWidth={1.5} />
                <span>
                  {DATE_FMT.format(start)} {TIME_FMT.format(start)}-{TIME_FMT.format(endsAt)}
                </span>
              </p>
            </div>
            <div className="flex flex-shrink-0 items-start gap-2">
              <span
                className={
                  'rounded-pill px-2.5 py-0.5 text-caption font-semibold ' + STATUS_TONE[booking.status]
                }
              >
                {STATUS_LABEL[booking.status]}
              </span>
              <ChevronRight className="mt-0.5 h-4 w-4 text-ink-muted" strokeWidth={1.75} />
            </div>
          </div>
        </div>
      </button>

      {canCancel && upcoming ? (
        <div className="px-4 pb-4">
          <Button
            variant="secondary"
            size="sm"
            width="full"
            loading={cancelling}
            onClick={onCancel}
          >
            Cancel booking
          </Button>
        </div>
      ) : canClear ? (
        <div className="px-4 pb-4">
          <Button
            variant="secondary"
            size="sm"
            width="full"
            loading={clearing}
            onClick={onClear}
          >
            Clear
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

function facilityStubFromBooking(booking: FacilityBooking): Facility {
  return {
    facility_id: booking.facility_id,
    tenant_id: '',
    location_id: '',
    name: booking.facility_name?.trim() || 'Facility booking',
    enabled: true,
    booking_mode: 'internal',
    external_open_method: 'browser',
    members_only: false,
    community_only: false,
    require_approval: booking.approval_required,
    allow_cancel: true,
    slot_interval_minutes: 60,
  };
}
